import React, { useEffect } from 'react';
import { Text } from '@cuemath/web-comps';

import FilterDropdown from 'comps/filter/new-filter-dropdown';
import { FilterField, FieldLabelText, UI } from '../../styled-comps';
import { countriesForRegions } from '../../constants';

// Searchable multi-select of countries, scoped to the currently-selected
// region(s). Renders nothing until at least one region is chosen. Picking
// countries narrows the funnel to those countries (ISO-2) and supersedes the
// region filter in the query (see helpers.buildRangeParams) — the region chips
// only decide which countries are on offer here.
//
// Uses the repo's shared `FilterDropdown` (the same searchable multi-select used
// by every other filter on this screen) for consistency. `value` is an array of
// ISO-2 codes owned by the parent; when a region is de-selected, any of its codes
// still in `value` are pruned (from the options shown and, via the effect, from
// parent state) so a stale country can never keep filtering after its region is
// gone.
const CountrySelect = ({ regions, value, onChange, width = '260px', label = 'Countries' }) => {
  const options = countriesForRegions(regions || []);
  const allowed = options.map(option => option.value);
  const validValue = (value || []).filter(code => allowed.indexOf(code) !== -1);

  // Prune stale codes from the parent once the offered set changes.
  const allowedKey = allowed.join('|');
  useEffect(() => {
    if (validValue.length !== (value || []).length) onChange(validValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey]);

  if (!regions || !regions.length) return null;

  return (
    <FilterField $width={width}>
      <FieldLabelText variant="eyebrow2">{label}</FieldLabelText>
      <FilterDropdown
        options_list={options}
        filter={{ value: validValue }}
        onChange={picked => onChange(picked || [])}
        multiple
        placeholder="All countries in region"
        noOptionsMessage={() => 'No countries for the selected region'}
        closeMenuOnSelect={false}
      />
      <Text variant="caption" color={UI.muted}>
        Filters by country; overrides the region filter when set
      </Text>
    </FilterField>
  );
};

export default CountrySelect;
